import * as React from "react";
import { BaseModal } from "../../../atoms/base-modal";
import { useState } from "react";
import { UploadIssueFile } from "../../../../application/domain/application/application-entity";
import {
  ExtensionMimeType,
  filenameToExtension,
  mimeTypeList,
} from "@graffer-inc/cloud-gov-core";
import {
  Checkbox,
  SingleFileUploader,
  PrimaryButton,
  SecondaryButton,
} from "@graffer-inc/console-design-kit";
import { featureFlag_EnableLgpkiSignature } from "../../../../feature-flags";
import { useAuthContextV2 } from "../../../../auth-state";

interface IssueFileUploadProps {
  isActive: boolean;
  onUpload: (issueFile: UploadIssueFile, skipNotification: boolean) => void;
  onClose: () => void;
  isLoading: boolean;
}

export const IssueFileUpload: React.FC<IssueFileUploadProps> = ({
  isActive,
  onUpload,
  onClose,
  isLoading,
}) => {
  const { auth } = useAuthContextV2();
  const [file, setFile] = React.useState<File | null>(null);
  const [fileDataURI, setFileDataURI] = React.useState<string | null>(null);
  const [error, setError] = useState<boolean>(false);

  const handleSetFile = (file: File | undefined) => {
    if (!file) {
      // file選択せずにキャンセルしたパターン
      return;
    }
    const fileReader = new FileReader();
    fileReader.onload = function () {
      const dataUri = this.result as string;
      setFile(file);
      setFileDataURI(dataUri.split(",")[1]);
    };
    fileReader.readAsDataURL(file);
  };

  const deleteFile = () => {
    setFile(null);
  };

  const [isLgpkiSigned, setIsLgpkiSigned] = useState(false);
  const [skipNotification, setSkipNotification] = useState(false);

  const isEnableLgpkiSignature = featureFlag_EnableLgpkiSignature(auth);

  const handleCloseModal = () => {
    deleteFile();
    onClose();
  };

  return (
    <BaseModal
      isActive={isActive}
      title="交付物アップロード"
      onClose={handleCloseModal}
    >
      <section className="modal-card-body">
        <div className="field">
          <SingleFileUploader
            acceptFileType={[...mimeTypeList]}
            deleteFile={deleteFile}
            description=""
            errorMessages=""
            label="交付物"
            required
            setFile={handleSetFile}
            value={file}
            maxFileSize={{
              size: 10,
              unit: "MB",
            }}
            onError={(e) => setError(e.hasError)}
          />
        </div>
        {isEnableLgpkiSignature && (
          <>
            <div className="spacer"></div>
            <div className="field">
              <Checkbox
                label={"職責証明書電子署名の検証"}
                description={
                  "「Graffer 職責証明書電子署名」を用いて電子署名された交付物をアップロードする場合は、チェックを入れてください。ここにチェックを入れることで、アップロードされた交付物の電子署名を申請者が検証出来るようになります。"
                }
                options={[
                  {
                    name: "職責証明書電子署名の検証を有効にする",
                    value: "isLgpkiSigned",
                  },
                ]}
                values={isLgpkiSigned ? ["isLgpkiSigned"] : []}
                onChange={() => setIsLgpkiSigned((oldValue) => !oldValue)}
              />
            </div>
          </>
        )}
        <div className="spacer"></div>
        <div className="field">
          <Checkbox
            label={"申請者への通知メールの自動送信"}
            description={
              "アップロードを行うと、交付物がダウンロード可能になった旨のメールが申請者に自動送信されます。メールを送信しない場合はチェックを外してください。"
            }
            options={[
              {
                name: "アップロード時に通知メールを自動送信する",
                value: "sendNotification",
              },
            ]}
            values={!skipNotification ? ["sendNotification"] : []}
            onChange={() => setSkipNotification((oldValue) => !oldValue)}
          />
        </div>
      </section>
      <footer className="modal-card-foot is-justify-content-center">
        <SecondaryButton
          text="キャンセル"
          onClick={() => {
            handleCloseModal();
          }}
        />
        <PrimaryButton
          text="アップロード"
          onClick={async () => {
            await onUpload(
              {
                type:
                  file!.type ||
                  (ExtensionMimeType as TODO<any>)[
                    filenameToExtension(file!.name)
                  ],
                name: file!.name,
                data: fileDataURI!,
                isLgpkiSigned: isLgpkiSigned,
              },
              skipNotification,
            );
            handleCloseModal();
          }}
          disabled={error}
          isLoading={isLoading}
        />
      </footer>
    </BaseModal>
  );
};
